import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import useAxios from "../../../../MainComponents/Hooks/useAxios.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import HelmetTags from "../../../../MainComponents/HelmetTags.jsx";

const HoldCodesTracker = () => {
  const [submitGenerate, setSubmitGenerate] = useState({
    flag: "",
    dependency: false,
    data: {},
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({ mode: "onTouched" });

  const api = process.env.REACT_APP_ADMIN_COURSE_HOLD_CODES_TRACKER_API;

  const [trackCodesSuccess, trackCodesErrors, submitLoading] = useAxios(
    api,
    "POST",
    submitGenerate.flag,
    submitGenerate.dependency,
    submitGenerate.data,
    true,
  );

  const payload = useMemo(() => {
    const res = trackCodesSuccess;
    if (!res || res?.status !== true) return null;
    return {
      message: res?.message ?? "",
      code: res?.data?.code ?? "",
      used: Boolean(res?.data?.used),
      courseTitle: res?.data?.course?.title ?? "-",
      courseId: res?.data?.course?.id ?? null,
      usedByName: res?.data?.used_by?.name ?? "-",
      usedById: res?.data?.used_by?.id ?? null,
      usedAt: res?.data?.used_at ?? "-",
    };
  }, [trackCodesSuccess]);

  useEffect(() => {
    if (payload) reset({ code: "" });
  }, [payload, reset]);

  const onSubmit = (data) => {
    setSubmitGenerate((prev) => ({
      flag: "trackCode",
      dependency: !prev.dependency,
      data,
    }));
  };

  const serverCodeError =
    trackCodesErrors?.response?.data?.errors?.code?.[0] ?? null;

  const serverMessage =
    trackCodesErrors?.response?.data?.message ??
    (trackCodesErrors ? "Something went wrong" : null);

  return (
    <section className="dark:bg-dark dark:text-light h-full w-full overflow-y-auto px-10 py-10 sm:px-4">
      <HelmetTags title={` Hold Codes Tracker | Mr.Mohamed Salama`} />

      <h2 className="mb-7 text-start text-2xl font-bold sm:text-center">
        Hold Codes Tracker
      </h2>

      <div className="form-codes-container my-20">
        <div className="form-codes flex w-full items-start justify-center gap-20 lg:flex-col lg:items-center lg:justify-start">
          <form
            method="post"
            onSubmit={handleSubmit(onSubmit)}
            className="shadow-l flex h-fit w-96 flex-col items-center justify-start gap-6 rounded-3xl bg-white p-10 shadow-blue/0 md:w-64"
          >
            <div className="first-row flex w-full items-start justify-between gap-6 md:flex-col md:justify-start">
              <div className="flex w-full flex-col items-end justify-center gap-2">
                <label className="w-full truncate" htmlFor="serial">
                  Code
                </label>

                <input
                  className="signin-inputs w-full"
                  type="text"
                  placeholder="code"
                  id="serial"
                  {...register("code", { required: true })}
                />

                {errors.code && (
                  <p className="text-xs text-red-500">
                    This field is required.
                  </p>
                )}

                {serverCodeError && (
                  <p className="w-full text-end text-xs text-red-500">
                    {serverCodeError}
                  </p>
                )}
              </div>
            </div>

            <button
              disabled={!isValid || submitLoading}
              className="submit mt-6 w-full"
              type="submit"
            >
              {submitLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                "Track"
              )}
            </button>

            {payload && (
              <div className="mt-8 w-full rounded-2xl border border-green-200 bg-green-50 p-6 text-sm text-green-900 shadow-sm transition-all">
                {/* Header */}
                <div className="mb-4 border-b border-green-200 pb-3">
                  <h3 className="text-base font-bold uppercase tracking-wide">
                    {payload.message || "Tracking Information"}
                  </h3>
                </div>

                {/* Data Grid */}
                <div className="grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-2">
                  {/* Column 1 */}
                  <div className="space-y-3">
                    <div className="flex flex-col">
                      <span className="text-xs font-bold uppercase opacity-60">
                        Code
                      </span>
                      <span className="font-mono text-lg font-bold tracking-wider">
                        {payload.code}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-xs font-bold uppercase opacity-60">
                        Status
                      </span>
                      <span
                        className={`font-semibold ${payload.used ? "text-red-600" : "text-green-700"}`}
                      >
                        {payload.used ? "● Used" : "● Not used"}
                      </span>
                    </div>
                  </div>

                  {/* Column 2 */}
                  <div className="space-y-3">
                    <div className="flex flex-col">
                      <span className="text-xs font-bold uppercase opacity-60">
                        Course
                      </span>
                      <span className="font-semibold">
                        {payload.courseTitle}
                        {payload.courseId && (
                          <span className="ml-1 text-xs opacity-70">
                            (#{payload.courseId})
                          </span>
                        )}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-xs font-bold uppercase opacity-60">
                        Used By
                      </span>
                      <span className="font-semibold">
                        {payload.usedByName}
                        {payload.usedById && (
                          <span className="ml-1 text-xs opacity-70">
                            (#{payload.usedById})
                          </span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Full Width Row */}
                  <div className="mt-2 border-t border-green-200 pt-3 md:col-span-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase opacity-60">
                        Usage Timestamp
                      </span>
                      <span className="font-medium italic">
                        {payload.usedAt}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!payload && serverMessage && (
              <div className="mt-4 w-full rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                {serverMessage}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default HoldCodesTracker;
